/**
 * Gestiona el estado de autenticación y la expiración de sesión en el navegador.
 *
 * @packageDocumentation
 */

import axios from "axios";

/**
 * ESTADO EN MEMORIA (se pierde al recargar página, es correcto)
 */
let accessToken = null;
let accessTokenExpMs = 0;

/**
 * Promesa en curso para evitar múltiples refresh simultáneos
 */
let refreshPromise = null;

/**
 * Cliente SOLO para auth (sin interceptores) para evitar bucles
 * IMPORTANTE: withCredentials true para que viaje la cookie HttpOnly refresh_token_api
 */
const authClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
    headers: { Accept: "application/json" },
});

/**
 * Decodifica una cadena en formato Base64 URL.
 *
 * @param input - Cadena codificada que se desea convertir.
 * @returns Contenido decodificado.
 */
function base64UrlDecode(input) {
    input = input.replace(/-/g, "+").replace(/_/g, "/");
    const pad = input.length % 4;
    if (pad) input += "=".repeat(4 - pad);
    return atob(input);
}

/**
 * Extrae la caducidad de un JWT expresada en milisegundos.
 *
 * @param token - Token JWT del que se obtiene la fecha de expiración.
 * @returns Marca temporal de expiración o `0` si el token no puede interpretarse.
 */
function parseJwtExpMs(token) {
    try {
        const payload = token.split(".")[1];
        const json = JSON.parse(base64UrlDecode(payload));
        return (json.exp || 0) * 1000;
    } catch {
        return 0;
    }
}

/**
 * Almacena el token de acceso y su caducidad en memoria.
 *
 * @param token - Token JWT recibido del backend o valor vacío para limpiarlo.
 */
export function setAccessToken(token) {
    accessToken = token || null;
    accessTokenExpMs = token ? parseJwtExpMs(token) : 0;
}

/**
 * Limpia token (por ejemplo, si refresh falla o en logout)
 */
export function clearAccessToken() {
    accessToken = null;
    accessTokenExpMs = 0;
}

/**
 * Devuelve el token en memoria sin intentar renovarlo.
 *
 * @returns Token vigente en memoria o `null` si no existe.
 */
export function getAccessToken() {
    return accessToken;
}

/**
 * Comprueba si el token seguirá siendo válido durante el margen indicado.
 *
 * @param skewMs - Margen mínimo de validez exigido en milisegundos.
 * @returns `true` cuando existe un token que supera el margen.
 */
export function isTokenValidSoon(skewMs = 60_000) {
    if (!accessToken || !accessTokenExpMs) return false;
    return accessTokenExpMs - Date.now() > skewMs;
}

/**
 * Obtiene un token válido:
 * - si hay token válido -> lo devuelve
 * - si no hay o está por caducar -> llama /auth/refresh (cookie HttpOnly)
 * - evita llamadas duplicadas con refreshPromise
 *
 * @param options - Opciones de renovación del token.
 * @returns Token válido o `null` cuando la sesión no puede renovarse.
 */
export async function obtenerTokenValido({ skewMs = 60_000 } = {}) {
    // 1) Si ya es válido, devolvemos
    if (isTokenValidSoon(skewMs)) return accessToken;

    // 2) Si ya hay refresh en curso, esperamos ese
    if (refreshPromise) return refreshPromise;

    // 3) Lanzamos refresh
    refreshPromise = (async () => {
        try {
            const { data } = await authClient.post("/auth/refresh");
            const newToken = data?.authorisation?.token;

            if (!newToken) {
                clearAccessToken();
                return null;
            }

            setAccessToken(newToken);
            return newToken;
        } catch (e) {
            // si refresh da 401/403, sesión caducada o inválida
            clearAccessToken();
            return null;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}
/**
 * BOOT para multi-página (Opción A):
 * se llama al cargar cada página para “rehidratar” access token usando la cookie HttpOnly.
 * No hace throw, no ensucia consola; solo prepara token si hay sesión.
 *
 * @returns Token rehidratado o `null` cuando no hay una sesión válida.
 */
export async function bootAuth() {
    if (isTokenValidSoon()) {
        return accessToken;
    }

    return await obtenerTokenValido({ skewMs: 60_000 });
}

/**
 * Logout local (solo memoria). Útil si backend devuelve 401 persistente.
 */
export function logoutLocal() {
    clearAccessToken();
}
